import { useCallback } from "react";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";
import { PanelVariableInputContent } from "../../lib/panel/panel-variable-input-content";
import { DoubleButtonsBlock } from "../double-buttons/types";
import { ButtonBlock } from "./types";
import { useTranslation } from "react-i18next";

interface EmailPanelLabelProps {
  buttonKey?: "button0" | "button1";
}

export function EmailPanelLabel({ buttonKey }: EmailPanelLabelProps = {}) {
  const blockProps = useBlockPanelPropsContext();
  const { t } = useTranslation("editor-blocks-library");
  const { formAPI } = blockProps;
  const block = blockProps.block as ButtonBlock | DoubleButtonsBlock;

  const locale = blockProps.locale || blockProps?.defaultLocale;
  const content = block.content?.[locale];

  // Get current values based on whether we're dealing with single or double button
  const currentText = buttonKey
    ? (
        content as {
          button0?: { text: string; href?: string };
          button1?: { text: string; href?: string };
        }
      )?.[buttonKey]?.text
    : (content as { text: string; href?: string })?.text;
  const currentHref = buttonKey
    ? (
        content as {
          button0?: { text: string; href?: string };
          button1?: { text: string; href?: string };
        }
      )?.[buttonKey]?.href
    : (content as { text: string; href?: string })?.href;

  const handleSave = useCallback(
    (text: string) => {
      if (buttonKey) {
        const doubleContent = content as {
          button0?: { text: string; href?: string };
          button1?: { text: string; href?: string };
        };
        formAPI.updateField(`${formAPI.blockPath}.content.${locale}`, {
          button0: doubleContent?.button0 || { text: "", href: "" },
          button1: doubleContent?.button1 || { text: "", href: "" },
          [buttonKey]: {
            text,
            href: currentHref || "",
          },
        });
      } else {
        formAPI.updateField(`${formAPI.blockPath}.content.${locale}`, {
          text,
          href: currentHref || "",
        });
      }
    },
    [formAPI, locale, buttonKey, currentHref, content],
  );

  const inputId = buttonKey ? `${buttonKey}-label` : "button-label";

  return (
    <PanelVariableInputContent
      triggerOnChange
      title={t("editorBlocksLibrary.blocks.button.panel.label.title", "Label")}
      value={currentText ?? ""}
      onChange={handleSave}
      variables={blockProps.variables}
      id={inputId}
      placeholder={t(
        "editorBlocksLibrary.blocks.button.panel.label.description",
        "Enter the button text",
      )}
      outputFormat="text"
    />
  );
}
